<script setup>
import DocumentService from '@/services/quality/document.service'
import { documentStatusColor, documentStatusTitle } from '@/enums/quality/document-status'
import DocumentReader from '@/views/quality/document/reader/DocumentReader.vue'
import DocumentControlBox from '@/views/quality/document/reader/DocumentControlBox.vue'
import DocumentToc from '@/views/quality/document/reader/DocumentToc.vue'
import DocumentInfoSection from '@/views/quality/document/sections/DocumentInfoSection.vue'
import DocumentCoverageSection from '@/views/quality/document/sections/DocumentCoverageSection.vue'
import DocumentVersionHistory from '@/views/quality/document/sections/DocumentVersionHistory.vue'
import DocumentReviewHistory from '@/views/quality/document/sections/DocumentReviewHistory.vue'
import DocumentApprovalSection from '@/views/quality/document/sections/DocumentApprovalSection.vue'
import DocumentVersionDrawer from '@/views/quality/document/DocumentVersionDrawer.vue'
import DocumentEditDrawer from '@/views/quality/document/DocumentEditDrawer.vue'

// meta.name is deliberately omitted: unplugin-vue-router derives the route name
// from the file path (app-quality-document-id) and the guard reads only
// meta.subject / meta.action. subject is 'document.menu', NOT
// 'quality.document.menu' — a 4-part permission drops its module segment when
// CASL rules are built, so the full dotted string blocks every non-super-admin.
definePage({
  meta: {
    subject: 'document.menu',
    action: 'register',
  },
})

const route = useRoute()
const router = useRouter()

const document = ref(null)
const versions = ref([])
const selectedVersion = ref(null)
const headings = ref([])
const isLoading = ref(false)

let fetchToken = 0

const fetchDocument = async () => {
  const token = ++fetchToken

  isLoading.value = true
  await DocumentService.show(route.params.id, data => {
    if (token !== fetchToken) return
    document.value = data?.data ?? data ?? null
  }, () => {
    if (token !== fetchToken) return
    isLoading.value = false
  })

  await DocumentService.listVersions(route.params.id, data => {
    if (token !== fetchToken) return
    versions.value = data?.data ?? data ?? []

    // Default to the current version — "the one I am allowed to follow" is the
    // question this page exists to answer, so it must be the landing state.
    //
    // But only on the FIRST load. This runs again after every lifecycle action
    // (the approval panel emits `changed`), and current_version_id only moves on
    // issue() — so re-deriving it every time would yank the user off the version
    // they just submitted or decided on, back to the previously-effective one,
    // the instant their action succeeded. Keep their selection if it still
    // exists, and re-read it from the refetched list so its status is current.
    const currentId = document.value?.current_version?.id
    const keepId = selectedVersion.value?.id

    selectedVersion.value = versions.value.find(v => v.id === keepId)
      ?? versions.value.find(v => v.id === currentId)
      ?? versions.value[0]
      ?? null
  })

  if (token === fetchToken) isLoading.value = false
}

fetchDocument()

const reviseSource = ref(null)
const isVersionDrawerOpen = ref(false)

const onReviseFrom = version => {
  reviseSource.value = version
  isVersionDrawerOpen.value = true
}

const canRevise = computed(() => document.value?.permissions?.revise?.allowed === true)

const isEditDrawerOpen = ref(false)

// The capability triple is the only authority — never recompute "can I edit?"
// from raw permissions here. DocumentCapabilityService::edit() is state-aware:
// it is false on an OBSOLETE document even for a user holding the permission.
const editCap = computed(() => document.value?.permissions?.edit ?? { visible: false, allowed: false, reason: '' })
</script>

<template>
  <div>
    <div
      v-if="document"
      class="d-flex align-center flex-wrap gap-3 mb-6"
    >
      <IconBtn @click="router.push({ name: 'app-quality-document' })">
        <VIcon icon="tabler-arrow-left" />
      </IconBtn>

      <div class="flex-grow-1">
        <div class="text-caption text-medium-emphasis">
          {{ document.code }}
        </div>
        <div class="d-flex align-center gap-2">
          <span class="text-h5">{{ document.title }}</span>
          <VChip
            :color="documentStatusColor(document.status)"
            size="small"
            label
          >
            <span>{{ documentStatusTitle(document.status) }}</span>
          </VChip>
        </div>
      </div>

      <div
        v-if="editCap.visible"
        class="d-flex flex-column align-end"
      >
        <VBtn
          :disabled="!editCap.allowed"
          variant="tonal"
          prepend-icon="tabler-edit"
          @click="isEditDrawerOpen = true"
        >
          Edit details
        </VBtn>

        <!--
          A blocked action stays visible and explains itself; it is never
          hidden. Matches DocumentApprovalSection's caption pattern — a
          VTooltip on a :disabled control never fires, since browsers do not
          dispatch hover events on disabled form controls.
        -->
        <p
          v-if="!editCap.allowed && editCap.reason"
          class="text-caption text-medium-emphasis mb-0 mt-1"
        >
          {{ editCap.reason }}
        </p>
      </div>
    </div>

    <VProgressLinear
      v-if="isLoading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <VRow v-if="document">
      <VCol
        cols="12"
        md="8"
      >
        <DocumentControlBox
          :document="document"
          :version="selectedVersion"
        />

        <DocumentReader
          :document="document"
          :version="selectedVersion"
          @headings="value => headings = value"
        />
      </VCol>

      <VCol
        cols="12"
        md="4"
      >
        <div class="d-flex flex-column gap-4">
          <!--
            @revise-from is what makes "Edit draft" work. Without it the panel's
            button renders (revise.visible is unconditionally true) and does
            nothing on click, and PUT .../versions/{version} is unreachable from
            any UI path — so a draft created with a typo, or with no file, has no
            recovery route at all.
          -->
          <DocumentApprovalSection
            :document="document"
            :version="selectedVersion"
            @changed="fetchDocument"
            @revise-from="onReviseFrom"
          />

          <DocumentToc :headings="headings" />

          <DocumentVersionHistory
            :versions="versions"
            :selected-id="selectedVersion?.id"
            :can-revise="canRevise"
            @select="version => selectedVersion = version"
            @revise-from="onReviseFrom"
          />

          <DocumentReviewHistory
            :document="document"
            :versions="versions"
            @changed="fetchDocument"
          />

          <DocumentInfoSection :document="document" />

          <DocumentCoverageSection :document="document" />
        </div>
      </VCol>
    </VRow>

    <DocumentVersionDrawer
      v-model:is-drawer-open="isVersionDrawerOpen"
      :document="document"
      :source-version="reviseSource"
      @saved="fetchDocument"
    />

    <DocumentEditDrawer
      v-model:is-drawer-open="isEditDrawerOpen"
      :document="document"
      @saved="fetchDocument"
    />
  </div>
</template>
